import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faStore, faShieldAlt, faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';

import AuthLayout from '../../components/AuthLayout';
import CompanyRepository from '../../repositories/CompanyRepository';
import { getSessionCompany, openProviderSession } from '../../services/sessionService';
import { cpfOrCnpjMask, onlyNumbers } from '../../utils/normalize';
import './styles.css';

export const PROVIDER_BENEFITS = [
  { icon: faStar, text: 'Veja como os clientes avaliam cada atendimento da sua oficina.' },
  { icon: faStore, text: 'Mostre fotos, serviços e horário na busca do Sobre Rodas.' },
  { icon: faShieldAlt, text: 'Acesso só com a senha da oficina, sem compartilhar com ninguém.' },
];

const STEP = { CNPJ: 'cnpj', PASSWORD: 'password', FIRST: 'first', FORGOT: 'forgot' };

const statusOf = (err) => err && err.response && err.response.status;

function Spinner({ busy, idle, label }) {
  return busy ? <><span className='auth-spinner' aria-hidden='true' />{label}</> : idle;
}

// Empresa escolhida, com atalho para trocar o CNPJ.
function CompanyChip({ company, onChange }) {
  return (
    <div className='pl-company'>
      <span className='pl-company-initial' aria-hidden='true'>{(company.tradingName || company.name || '?').charAt(0)}</span>
      <span className='pl-company-text'>
        <strong>{company.tradingName || company.name}</strong>
        <span>{cpfOrCnpjMask(company.cpfCnpj)}</span>
      </span>
      <button type='button' className='auth-link' onClick={onChange}>Trocar</button>
    </div>
  );
}

function CnpjStep({ onFound }) {
  const [cnpj, setCnpj] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const digits = onlyNumbers(cnpj);
  const valid = digits.length === 11 || digits.length === 14;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      onFound(await CompanyRepository.findByCnpj(digits));
    } catch (err) {
      const status = statusOf(err);
      if (status === 404) setError('Não encontramos uma oficina com esse CNPJ. Confira os números.');
      else if (status === 429) setError('Muitas consultas seguidas. Aguarde alguns minutos e tente de novo.');
      else setError('Não foi possível consultar agora. Tente de novo em instantes.');
      setBusy(false);
    }
  };

  return (
    <form className='auth-form' onSubmit={handleSubmit} noValidate>
      <header className='auth-form-header'>
        <h2>Entrar na área da oficina</h2>
        <p>Informe o CNPJ da sua empresa para continuar.</p>
      </header>
      <div className='auth-field'>
        <label htmlFor='pl-cnpj'>CNPJ ou CPF</label>
        <input
          id='pl-cnpj'
          inputMode='numeric'
          autoComplete='username'
          placeholder='00.000.000/0000-00'
          value={cpfOrCnpjMask(digits)}
          maxLength={18}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? 'pl-cnpj-error' : undefined}
          onChange={(event) => { setCnpj(onlyNumbers(event.target.value).slice(0, 14)); setError(''); }}
        />
        {error && <p id='pl-cnpj-error' className='pl-error' role='alert'>{error}</p>}
      </div>
      <button type='submit' className='auth-submit' disabled={!valid || busy} aria-busy={busy}>
        <Spinner busy={busy} idle='Continuar' label='Consultando…' />
      </button>
      <p className='auth-signup'>
        É cliente e quer ver o seu veículo? <a className='auth-link' href='/client/login'>Entrar na minha garagem</a>
      </p>
    </form>
  );
}

function PasswordStep({ company, onBack, onForgot, onLogged }) {
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current && inputRef.current.focus(); }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError('');
    try {
      const session = await CompanyRepository.login({ cpfCnpj: company.cpfCnpj, sourceId: company.sourceId, password });
      onLogged(session);
    } catch (err) {
      const status = statusOf(err);
      if (status === 401) setError('Senha incorreta. Confira e tente de novo.');
      else if (status === 429) setError('Muitas tentativas. Aguarde alguns minutos ou redefina a senha.');
      else setError('Não foi possível entrar agora. Tente de novo em instantes.');
      setBusy(false);
    }
  };

  return (
    <form className='auth-form' onSubmit={handleSubmit} noValidate>
      <header className='auth-form-header'>
        <h2>Digite sua senha</h2>
      </header>
      <CompanyChip company={company} onChange={onBack} />
      <div className='auth-field'>
        <div className='auth-field-row'>
          <label htmlFor='pl-password'>Senha</label>
          <button type='button' className='auth-link' onClick={onForgot}>Esqueci a senha</button>
        </div>
        <div className='pl-password'>
          <input
            id='pl-password'
            ref={inputRef}
            type={visible ? 'text' : 'password'}
            autoComplete='current-password'
            value={password}
            maxLength={72}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? 'pl-password-error' : undefined}
            onChange={(event) => { setPassword(event.target.value); setError(''); }}
          />
          <button type='button' className='auth-link pl-toggle' onClick={() => setVisible((value) => !value)} aria-pressed={visible}>
            {visible ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {error && <p id='pl-password-error' className='pl-error' role='alert'>{error}</p>}
      </div>
      <button type='submit' className='auth-submit' disabled={!password || busy} aria-busy={busy}>
        <Spinner busy={busy} idle='Entrar' label='Entrando…' />
      </button>
    </form>
  );
}

// Primeiro acesso e esqueci a senha: a API manda o link ao e-mail cadastrado.
function LinkStep({ company, firstAccess, onBack }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const email = company.maskedEmail ? <strong>{company.maskedEmail}</strong> : 'o e-mail cadastrado';

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      await CompanyRepository.requestAccessLink({ cpfCnpj: company.cpfCnpj, sourceId: company.sourceId });
      setSent(true);
    } catch (err) {
      setError(statusOf(err) === 429
        ? 'Já enviamos alguns links agora há pouco. Aguarde alguns minutos.'
        : 'Não foi possível enviar o link. Tente de novo em instantes.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className='auth-form'>
        <div className='pl-sent' role='status'>
          <span className='pl-sent-icon' aria-hidden='true'><FontAwesomeIcon icon={faEnvelopeOpenText} /></span>
          <h2>Confira seu e-mail</h2>
          <p>Enviamos para {email} um link para {firstAccess ? 'criar' : 'redefinir'} a senha. Se não chegar em alguns minutos, veja a caixa de spam.</p>
        </div>
        <button type='button' className='pl-secondary' onClick={onBack}>Voltar ao login</button>
      </div>
    );
  }

  return (
    <div className='auth-form'>
      <header className='auth-form-header'>
        <h2>{firstAccess ? 'Primeiro acesso' : 'Redefinir a senha'}</h2>
        <p>
          {firstAccess
            ? <>Sua oficina ainda não tem senha. Enviaremos para {email} um link para criá-la.</>
            : <>Enviaremos para {email} um link para criar uma senha nova.</>}
        </p>
      </header>
      <CompanyChip company={company} onChange={onBack} />
      {error && <p className='pl-error' role='alert'>{error}</p>}
      <button type='button' className='auth-submit' onClick={send} disabled={busy} aria-busy={busy}>
        <Spinner busy={busy} idle='Enviar link' label='Enviando…' />
      </button>
      <p className='pl-help'>
        {Number(company.sourceId) === 2
          ? 'O e-mail não é mais esse? Atualize o cadastro da empresa no OS Digital.'
          : 'O e-mail não é mais esse? Fale com o suporte do Sobre Rodas.'}
      </p>
    </div>
  );
}

// Login da oficina: CNPJ, depois senha (ou link de primeiro acesso / esqueci a senha).
export default function ProviderLogin() {
  const history = useHistory();
  const [step, setStep] = useState(STEP.CNPJ);
  const [company, setCompany] = useState(null);

  // Ja logada: vai direto para a area da oficina.
  useEffect(() => {
    let active = true;
    getSessionCompany()
      .then((sessionCompany) => { if (active && sessionCompany) history.replace('/provider/home'); })
      .catch(() => {});
    return () => { active = false; };
  }, [history]);

  const handleFound = (found) => {
    setCompany(found);
    setStep(found.hasAccess ? STEP.PASSWORD : STEP.FIRST);
  };

  const backToCnpj = () => {
    setCompany(null);
    setStep(STEP.CNPJ);
  };

  const handleLogged = (session) => {
    // Senha provisória dada pelo suporte: cria a senha nova antes de entrar.
    if (session.mustChangePassword) {
      window.location.href = `/provider/newpassword?token=${encodeURIComponent(session.token)}`;
      return;
    }
    openProviderSession(session);
    window.location.href = '/provider/home';
  };

  return (
    <AuthLayout eyebrow='Área da oficina' title='Sua oficina,' highlight='bem avaliada.' benefits={PROVIDER_BENEFITS}>
      {step === STEP.CNPJ && <CnpjStep onFound={handleFound} />}
      {step === STEP.PASSWORD && (
        <PasswordStep company={company} onBack={backToCnpj} onForgot={() => setStep(STEP.FORGOT)} onLogged={handleLogged} />
      )}
      {step === STEP.FIRST && <LinkStep company={company} firstAccess onBack={backToCnpj} />}
      {step === STEP.FORGOT && <LinkStep company={company} onBack={() => setStep(STEP.PASSWORD)} />}
    </AuthLayout>
  );
}
